import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { LearnerService } from 'src/app/shared/services/learner/learner.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { passwordLengthValidator, passwordsMatchValidator } from 'src/app/shared/validators';
import { AuthService } from '../../auth.service';
import { LearnerInvitationVerifyDto, LearnerPortalRegistrationDto } from '../models/learner-portal-registration-dto';

@Component({
  selector: 'app-learner-portal-registration',
  templateUrl: './learner-portal-registration.component.html',
  styleUrls: ['./learner-portal-registration.component.scss'],
  standalone: false,
})
export class LearnerPortalRegistrationComponent implements OnInit {
  learnerPortalRegistration: LearnerPortalRegistrationDto;
  isBusy = false;
  formGroup = new UntypedFormGroup(
    {
      invitationId: new UntypedFormControl('', { validators: [Validators.required] }),
      learnerUserId: new UntypedFormControl(''),
      password: new UntypedFormControl('', { validators: [Validators.required] }),
      confirmedPassword: new UntypedFormControl('', { validators: [Validators.required] }),
      hasPassword: new UntypedFormControl(false),
    },
    {
      updateOn: 'blur',
    }
  );

  invitationVerifyDto: LearnerInvitationVerifyDto;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private learnerService: LearnerService,
    private notificationService: NotificationService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    const invitationId = this.route.snapshot.paramMap.get('invitationId');

    this.learnerPortalRegistration = {
      invitationId: invitationId,
    } as LearnerPortalRegistrationDto;

    this.learnerService.validateInvitation(invitationId).subscribe(
      (response) => {
        if (response.succeeded) {
          this.invitationVerifyDto = response.value;

          // Redirect user to login page if the invitation has already been accepted
          if (response.value.isConfirmed) {
            this.redirectToLogin();
            return;
          }

          if (this.invitationVerifyDto.hasPassword) {
            this.formGroup.controls.confirmedPassword.clearValidators();
          } else {
            this.formGroup.controls.password.setValidators([Validators.required, passwordLengthValidator]);
            this.formGroup.controls.confirmedPassword.setValidators(Validators.required);
            this.formGroup.setValidators(passwordsMatchValidator);
          }

          this.formGroup.controls.password.updateValueAndValidity();
          this.formGroup.controls.confirmedPassword.updateValueAndValidity();
          this.formGroup.updateValueAndValidity();

          this.learnerPortalRegistration.learnerUserId = response.value.learnerUserId;
          this.formGroup.patchValue(this.learnerPortalRegistration);
          this.formGroup.controls.hasPassword.setValue(this.invitationVerifyDto.hasPassword);
        } else {
          this.notificationService.error(response.errors?.map((x) => x.description).join(','));
        }
      },
      () => {
        this.notificationService.error('Unable to verify invitation.');
      }
    );
  }

  onRegister() {
    if (!this.formGroup.valid) {
      this.formGroup.markAllAsTouched();
      this.notificationService.error('Please complete all required fields.');
      return;
    }

    const registrationDto = this.formGroup.value as LearnerPortalRegistrationDto;

    if (registrationDto.learnerUserId === null && registrationDto.password !== registrationDto.confirmedPassword) {
      this.notificationService.error('Passwords do not match');
      return;
    }

    this.isBusy = true;

    this.learnerService.confirmInvitation(registrationDto).subscribe(
      (response) => {
        if (response.succeeded) {
          this.notificationService.success('Confirmation succeeded');
          this.redirectToLogin();
        } else {
          this.isBusy = false;
          this.notificationService.error(response.errors?.map((x) => x.description).join(','));
        }
      },
      () => {
        this.isBusy = false;
        this.notificationService.error('Unable to confirm invitation.');
      }
    );
  }

  redirectToLogin() {
    this.router.navigate(['/auth/family/login']);
  }
}
